import { useLayoutEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Column, Row, Spacer } from "../../../../components/Layout/Layout";
import {
  ResponseCenterDistributionDetails,
  ResponseCenterDistributionParams,
} from "../../../../core/api/responsecenters/types";
import {
  validateEmailIfExists,
  validatePhoneNumber,
} from "../../../../core/helpers/validation";
import useTranslations from "../../../../core/i18n/useTranslations";
import Checkbox from "../../../../ui-lib/components/Checkbox/Checkbox";
import TextInput from "../../../../ui-lib/components/Inputs/TextInput";
import { DistributorSettings } from "./DistributorSettings";
import PrimeModal from "../../../../ui-lib/components/PrimeModal/PrimeModal";
import LoadingSpinner from "../../../../ui-lib/components/Loading/LoadingSpinner";

export const DistributorForm = ({
  isOpen,
  labels,
  defaultValues,
  dataLoading,
  onSubmit,
  onClose,
}: {
  isOpen: boolean;
  labels: {
    title: string;
    description?: string;
    submit: string;
  };
  defaultValues?: ResponseCenterDistributionDetails;
  dataLoading?: boolean;
  onSubmit: (values: ResponseCenterDistributionParams) => Promise<void>;
  onClose: () => void;
}) => {
  const t = useTranslations();

  const methods = useForm<ResponseCenterDistributionParams>({
    defaultValues: {
      contactGetAuthorizedUrl: false,
      sendResponseAlarm: true,
      onResponse: true,
      onProperty: true,
      onCare: true,
      onService: false,
    },
  });
  const {
    control,
    formState: { isSubmitting, errors },
    handleSubmit,
    reset,
  } = methods;

  useLayoutEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues]);

  return (
    <PrimeModal
      withFooter
      withHeader
      header={labels.title}
      isOpen={isOpen}
      onClose={onClose}
      fixedWidth="848px"
      loading={isSubmitting}
      submitBtn={{
        text: labels.submit,
        onClick: handleSubmit(onSubmit),
      }}
      cancelBtn={{
        text: t("Common:cancel"),
        onClick: onClose,
      }}
    >
      {labels.description && (
        <>
          <Spacer size={16} />
          <p className="grey-600">{labels.description}</p>
        </>
      )}

      <Spacer size={16} />
      {dataLoading ? (
        <LoadingSpinner theme="primary" />
      ) : (
        <FormProvider {...methods}>
          <form>
            <Row align="start">
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <TextInput
                    required
                    inputRef={field.ref}
                    label={t("Common:name")}
                    placeholder={t(
                      "AlarmReceptions:distributor_input_name_placeholder",
                    )}
                    value={field.value}
                    onChange={field.onChange}
                    validationError={
                      errors.name && t("Errors:input_field_required")
                    }
                  />
                )}
              />
              <Spacer size={32} />
              <Controller
                name="email"
                control={control}
                rules={{
                  maxLength: 60,
                  validate: validateEmailIfExists,
                }}
                render={({ field }) => (
                  <TextInput
                    inputRef={field.ref}
                    label={t("Common:email")}
                    placeholder={t("Common:placeholder_email")}
                    value={field.value}
                    onChange={field.onChange}
                    validationError={
                      errors.email && t("Errors:input_email_format")
                    }
                  />
                )}
              />
            </Row>

            <Spacer size={16} />

            <Row align="start">
              <Controller
                name="telephonenumber"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value || value.length === 0) {
                      return true;
                    }

                    return validatePhoneNumber(value);
                  },
                }}
                render={({ field }) => (
                  <TextInput
                    inputRef={field.ref}
                    label={t("Common:labels_phone_number")}
                    placeholder={t("Common:input_phone_description")}
                    value={field.value}
                    onChange={field.onChange}
                    validationError={
                      errors?.telephonenumber &&
                      t("Errors:response_invalid_telephone_number_format")
                    }
                  />
                )}
              />
              <Spacer size={32} />
              <Column className="w-100" />
            </Row>

            <Spacer size={16} />

            <Row type="left" className="w-100">
              <Controller
                name="contactGetAuthorizedUrl"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    label={t("AlarmReceptions:distributor_input_get_auth_url")}
                    checked={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </Row>

            <Spacer size={16} />

            <DistributorSettings />
          </form>
        </FormProvider>
      )}
    </PrimeModal>
  );
};
